import React from 'react'
import { useState } from 'react'

import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'

import {
  Box,
  DateRangePicker,
  HStack,
  Select,
  SelectOption,
  Stack,
  Text,
  calendarType,
} from '@enechain/ecloud-designsystem'

type Props = {
  fromDate?: Date
  toDate?: Date
  onChange: (fromDate?: Date, toDate?: Date, filterType?: FilterType) => void
}

type FilterType = 'THIS_MONTH' | 'NEXT_MONTH' | 'CUSTOM' | 'ALL'

export const ContractPeriodFilter: React.FC<Props> = ({
  fromDate,
  toDate,
  onChange,
}) => {
  const [filterType, setFilterType] = useState<FilterType>('ALL')
  const { t } = useTranslation(['page'])
  const ContractPeriodFilterOptions: SelectOption[] = [
    { label: t('page:contract_list.filter.all'), value: 'ALL' },
    { label: t('page:contract_list.filter.this_month'), value: 'THIS_MONTH' },
    { label: t('page:contract_list.filter.next_month'), value: 'NEXT_MONTH' },
    { label: t('page:contract_list.filter.custom'), value: 'CUSTOM' },
  ]

  return (
    <HStack>
      <Stack gap="2">
        <Box width="200px">
          <Select<SelectOption, false>
            isMulti={false}
            options={ContractPeriodFilterOptions}
            onChange={(value): void => {
              const selectedType = value?.value as FilterType
              setFilterType(selectedType)

              const today = new Date()
              const year = today.getFullYear()
              const month = today.getMonth()

              switch (selectedType) {
                case 'THIS_MONTH': {
                  const fromDate = new Date(year, month, 1)
                  const toDate = new Date(year, month + 1, 0)
                  onChange(fromDate, toDate, 'THIS_MONTH')
                  break
                }
                case 'NEXT_MONTH': {
                  const fromDate = new Date(year, month + 1, 1)
                  const toDate = new Date(year, month + 2, 0)
                  onChange(fromDate, toDate, 'NEXT_MONTH')
                  break
                }
                case 'ALL': {
                  onChange(undefined, undefined, 'ALL')
                  break
                }
                case 'CUSTOM': {
                  onChange(fromDate, toDate, 'CUSTOM')
                  break
                }
                default:
                  break
              }
            }}
            selectedOptions={
              ContractPeriodFilterOptions.find(
                (option) => option.value === filterType,
              ) ?? null
            }
          />
        </Box>
        {(filterType === 'THIS_MONTH' || filterType === 'NEXT_MONTH') &&
          fromDate !== undefined &&
          toDate !== undefined && (
            <Text>
              {format(fromDate, 'yyyy/MM/dd')} ~ {format(toDate, 'yyyy/MM/dd')}
            </Text>
          )}
      </Stack>
      {filterType === 'CUSTOM' && (
        <Box width="200px">
          <DateRangePicker
            calendarType={calendarType.MONTH}
            from={fromDate}
            to={toDate}
            onChange={(from?: Date, to?: Date): void => {
              if (filterType === 'CUSTOM') {
                onChange(from, to, 'CUSTOM')
              }
            }}
          />
        </Box>
      )}
    </HStack>
  )
}
